import { useEffect, useRef } from 'react'
import { Button, Flex, Result, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import type { RouterLogin } from '@/features/portal/accountVoucher'
import { loginToMikrotik } from '@/features/portal/mikrotik'
import type { ChapChallenge } from '@/shared/lib/chap'
import { formatBytes, formatDuration } from '@/shared/lib/format'

interface SuccessStepProps {
  result: RouterLogin
  loginUrl: string
  dst: string | null
  chap: ChapChallenge | null
}

/**
 * Muvaffaqiyat ekrani — vazifaning eng muhim talabi shu yerda bajariladi:
 *
 * "MikroTik formasiga yuborishdan oldin backend javobini TO'LIQ kut" —
 * bu komponent FAQAT `hasAccess(result)` tasdiqlangach chaqiriladi
 * (`PortalPage`), ya'ni login+parol allaqachon qo'lda.
 *
 * "yuklanmoqda holatini aniq ko'rsat" — mehmon avval "Kirish berildi!"
 * ekranini (bir necha soniya) ko'radi, keyingina forma yuboriladi —
 * sahifa darhol boshqa domenga sakramaydi.
 *
 * "yuborish muvaffaqiyatsiz bo'lsa qayta urinish tugmasini ber" — pastdagi
 * "Ulanish" tugmasi doim ko'rinadi va qo'lda bosilishi mumkin (router
 * navigatsiyasi — muvaffaqiyat/muvaffaqiyatsizligini JS orqali bilib
 * bo'lmaydi, chunki sahifa ketadi; shuning uchun "muvaffaqiyatsiz bo'lsa"
 * signalini React aniqlay olmaydi va o'rniga har doim qo'lda ishlaydigan
 * zaxira beriladi).
 */
export function SuccessStep({ result, loginUrl, dst, chap }: SuccessStepProps) {
  const { t } = useTranslation()
  const submittedRef = useRef(false)

  const submit = (): void => {
    loginToMikrotik({ loginUrl, username: result.login, password: result.password, dst, chap })
  }

  useEffect(() => {
    if (submittedRef.current) return
    submittedRef.current = true
    const id = window.setTimeout(submit, 900)
    return () => window.clearTimeout(id)
    // Faqat bir marta, komponent umriga bog'liq — qayta ishga tushirilmaydi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
    ⚠️ KVOTASIZ KIRISH — "hech narsa yozmaslik" DEGANI EMAS.

    Ilgari bu blok `hasQuota` sharti ostida turardi va ikkala maydon
    ham bo'lmaganda BUTUNLAY tushib qolardi. Ya'ni cheksiz kirish
    olgan mehmon o'ziga qancha berilganini umuman ko'rmasdi — ekranda
    faqat "Kirish berildi!" qolardi va u "demak chegara bor, shunchaki
    aytishmadi" deb o'qiladi (mehmon eng ehtiyotkor taxminni qiladi:
    aslida CHEKLOVSIZ turib, u tejab ishlatadi).

    ⚠️ Bu `null` ning ma'nosi taxmin emas: kabinetda ham xuddi shunday
    o'qiladi (`DashboardPage.remainingLabel` — "`bytes: null` CHEKSIZ,
    `0` esa TUGAGAN"). Va matn ALLAQACHON uch tilda tayyor turgan edi
    (`success.unlimited`) — faqat hech kim uni ekranga chiqarmagan.
  */
  const hasQuota = result.quotaBytes != null || result.quotaSeconds != null

  return (
    <Result
      status="success"
      title={t('success.title')}
      subTitle={t('success.subtitle')}
      extra={
        <Flex vertical gap={10}>
          <Flex vertical gap={2} style={{ textAlign: 'center', marginBottom: 4 }}>
            {!hasQuota ? (
              <Typography.Text type="secondary">{t('success.unlimited')}</Typography.Text>
            ) : null}
            {result.quotaBytes != null ? (
              <Typography.Text type="secondary">
                {t('success.quotaBytes', { amount: formatBytes(result.quotaBytes) })}
              </Typography.Text>
            ) : null}
            {result.quotaSeconds != null ? (
              <Typography.Text type="secondary">
                {t('success.quotaSeconds', { amount: formatDuration(result.quotaSeconds) })}
              </Typography.Text>
            ) : null}
          </Flex>
          <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
            {t('success.manualHint')}
          </Typography.Text>
          <Button type="primary" size="large" block onClick={submit}>
            {t('success.manualButton')}
          </Button>
        </Flex>
      }
    />
  )
}
